import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/** Above Explore Unknown map overlays (z-index ~13k); portal to body avoids transformed-ancestor clipping. */
const GRID_READOUT_Z_INDEX = 500000;

/**
 * Same idea as `GridOverlay.js` (BattleUI dev grid): yellow lines + cell index,
 * but coordinates are in the **logical canvas** (e.g. 1920×1080) inside the scaled viewport,
 * so you can report e.g. "portal at cells (42, 18)–(55, 40)" for exploreUnknownPortalLayout.js.
 *
 * Coordinate readout is fixed to the **center of the browser viewport** (via portal) so it stays
 * visible regardless of CSS transforms on the game shell.
 */
function ExploreUnknownGridOverlay({
  containerRef,
  logicalWidth = 1920,
  logicalHeight = 1080,
  gridSize = 20,
  isVisible = false,
  zIndex = 400,
  tooltipFooter,
}) {
  const [mouseLogical, setMouseLogical] = useState({ x: 0, y: 0 });

  const updateFromEvent = useCallback(
    (e) => {
      const el = containerRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      let x = ((e.clientX - rect.left) / rect.width) * logicalWidth;
      let y = ((e.clientY - rect.top) / rect.height) * logicalHeight;
      x = Math.max(0, Math.min(logicalWidth, x));
      y = Math.max(0, Math.min(logicalHeight, y));
      setMouseLogical({ x, y });
    },
    [containerRef, logicalWidth, logicalHeight]
  );

  useEffect(() => {
    if (!isVisible) return undefined;

    const onMove = (e) => updateFromEvent(e);
    document.addEventListener('mousemove', onMove, { passive: true });
    return () => document.removeEventListener('mousemove', onMove);
  }, [isVisible, updateFromEvent]);

  if (!isVisible) {
    return null;
  }

  const verticalLines = [];
  for (let x = 0; x <= logicalWidth; x += gridSize) {
    verticalLines.push(x);
  }
  const horizontalLines = [];
  for (let y = 0; y <= logicalHeight; y += gridSize) {
    horizontalLines.push(y);
  }

  const cellX = Math.floor(mouseLogical.x / gridSize);
  const cellY = Math.floor(mouseLogical.y / gridSize);
  const snapX = cellX * gridSize;
  const snapY = cellY * gridSize;

  const readout = (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.92)',
        color: '#ffff00',
        padding: '12px 16px',
        borderRadius: 6,
        fontSize: 14,
        fontFamily: 'monospace',
        pointerEvents: 'none',
        zIndex: GRID_READOUT_Z_INDEX,
        maxWidth: 'min(360px, 90vw)',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.65)',
        border: '1px solid rgba(255, 255, 0, 0.35)',
      }}
    >
      <div style={{ fontWeight: 700 }}>
        Canvas px: {Math.round(mouseLogical.x)}, {Math.round(mouseLogical.y)}
      </div>
      <div>
        Cell (col, row): {cellX}, {cellY}
      </div>
      <div style={{ color: '#9cf', marginTop: 8, fontSize: 12, lineHeight: 1.35 }}>
        {tooltipFooter ||
          `Grid ${gridSize}px · same idea as BattleUI dev grid · report cells or canvas px for the portal`}
      </div>
    </div>
  );

  return (
    <>
      <div
        className="explore-unknown-layout-grid"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex,
          opacity: 0.85,
        }}
        aria-hidden
      >
        {verticalLines.map((x) => (
          <div
            key={`v-${x}`}
            style={{
              position: 'absolute',
              left: x,
              top: 0,
              width: 1,
              height: '100%',
              backgroundColor: '#ffff00',
              opacity: 0.35,
            }}
          />
        ))}
        {horizontalLines.map((y) => (
          <div
            key={`h-${y}`}
            style={{
              position: 'absolute',
              left: 0,
              top: y,
              width: '100%',
              height: 1,
              backgroundColor: '#ffff00',
              opacity: 0.35,
            }}
          />
        ))}

        <div
          style={{
            position: 'absolute',
            left: snapX,
            top: snapY,
            width: gridSize,
            height: gridSize,
            border: '2px solid #ff0000',
            backgroundColor: 'rgba(255, 0, 0, 0.12)',
            boxSizing: 'border-box',
          }}
        />
      </div>
      {typeof document !== 'undefined' ? createPortal(readout, document.body) : null}
    </>
  );
}

export default ExploreUnknownGridOverlay;
